// Drives the brawl in the browser: runs each question's web search, decides whose
// turn it is, streams each turn from the Worker (paid from the user's credit), and
// renders the three chat boxes, their influence bars, the vote board, the judge and
// the verdict, and the same brawl as one group chat (src/thread.js).
import { IDS, speakingOrder, parseTags, tally, influence } from "../lib/protocol.js";
import { SEATS, modelFor, starterFor, STARTER_NAMES, JUDGES, JUDGE_STARTER, judgeFor, judgeFree } from "../lib/models.js";
import * as settingsStore from "./settings.js";
import { startCosmos } from "./cosmos.js";
import { playIntro } from "./intro.js";
import { columnText, brawlMarkdown, exportFileName, shareText } from "./export.js";
import { createThread } from "./thread.js";
import { createPacer, CHARS_PER_SECOND } from "./smooth.js";
import { parseSample, drawSample } from "./sample.js";
import { renderBody, visibleText, escapeHtml } from "./body.js";

const $ = (s) => document.querySelector(s);
const arena = $("#arena");
const statusEl = $("#status");
const boxes = {};
const chat = createThread($("#chat"));
const latestBubble = {}; // each seat's newest message in the chat, which its votes go to
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
// Turns are written out at reading pace (src/smooth.js), with reduced motion too: text
// appearing at reading speed isn't the movement that setting is about, and without the
// pace a whole turn lands at once. The browser tests ask for it at once, in
// sessionStorage, to run quickly.
const instantText = (() => {
  try {
    return window.sessionStorage.getItem("chat-brawlers.pace") === "instant";
  } catch {
    return false;
  }
})();

// A failed turn. `kind` says what the user can do about it: sign in again, add
// credit, pick another model or wait out a rate limit pause the brawl, and Resume
// retries the turn.
class TurnError extends Error {
  constructor(kind, message) {
    super(message);
    this.kind = kind;
  }
}
const FIXABLE = new Set(["auth", "credits", "locked", "model", "rate"]);
const STOPPED = "stopped";

let choices = settingsStore.load();
let webOn = settingsStore.loadWeb(); // "Search the web", as the user left it
let replyLength = settingsStore.loadLength(); // "short" (chat-length turns) or "long"
let writeSpeed = settingsStore.loadSpeed(); // "normal", "fast" or "instant"
const SPEED_FACTOR = { normal: 1, fast: 2 };
let site = { signin: false, grant: 0, margin: 0, lock: false, search: false, searchCost: 0, searchesPerDay: 0, searchMonthUsed: false };
let account = { signedIn: false, email: "", balance: 0, unlocked: false };
let run = null; // { question, mode, maxRounds, transcript, sources, pending, paused, stopped, done, aborts, out, fails }

// The space background, coloured to match the seats.
const cosmos = startCosmos($("#cosmos"), {
  seats: IDS,
  colors: Object.fromEntries(IDS.map((id) => [id, getComputedStyle(document.documentElement).getPropertyValue(`--${id}`).trim()])),
  reducedMotion,
});

// `alert`: something the user has to act on before the brawl can go on, drawn so it
// can't be missed; the next status clears it.
// `busy`: something is still under way (the web search), so the line pulses.
const setStatus = (text, { alert = false, busy = false } = {}) => {
  statusEl.textContent = text;
  statusEl.classList.toggle("alert", alert);
  statusEl.classList.toggle("busy", busy);
};
const money = (micro) => `$${(micro / 1e6).toFixed(2)}`;
// A turn usually costs a fraction of a cent to a few cents: show it to a hundredth of a cent.
const turnMoney = (micro) => (micro < 1_000_000 ? `$${(micro / 1e6).toFixed(4)}` : money(micro));
const price = ([i, o]) => `$${i} / $${o}`;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Until the account adds credit only the starter models run; a seat whose pick is
// locked runs its starter, and the pick comes back once credit is added.
const isLocked = (m) => site.lock && !account.unlocked && !m.starter;
// Web search runs on starting credit too (it costs little), and is off while the
// month's searches (everyone's) or this user's day are used up: why it's off, or "".
let searchSpent = null; // "month" or "day", once a search came back saying so
const searchOff = () =>
  site.searchMonthUsed || searchSpent === "month" ? "monthly limit reached"
  : searchSpent === "day" ? "today's searches used"
  : "";
const searching = () => site.search && webOn && !searchOff();
const modelOf = (id) => {
  const m = modelFor(id, choices[id]);
  return isLocked(m) ? starterFor(id) : m;
};
const modelIds = () => Object.fromEntries(IDS.map((id) => [id, modelOf(id).id]));
const nameOf = (id) => modelOf(id).name;
const seatNames = () => Object.fromEntries(IDS.map((id) => [id, nameOf(id)]));
const canStart = () => account.signedIn && account.balance > 0;

// The session's web sources in the order the models number them (lib/protocol.js): each
// earlier question's, then this one's. `cite(n)` is source n, for linking citations.
const sessionSources = () => [...(run?.earlier ?? []).flatMap((e) => e.sources?.results ?? []), ...(run?.sources?.results ?? [])];
const citations = () => {
  const list = sessionSources();
  return (n) => list[n - 1];
};

// ---- rendering -----------------------------------------------------------

function buildArena() {
  arena.innerHTML = "";
  for (const id of IDS) {
    const el = document.createElement("article");
    el.className = "box";
    el.dataset.seat = id;
    el.style.setProperty("--c", `var(--${id})`);
    el.innerHTML = `
      <div class="influence" title="Influence: the votes behind this model's verdict right now, as on the vote board. Each model has one vote, its latest, and may back itself. It measures support, not truth.">
        <div class="influence-track"><div class="influence-fill"></div></div>
        <span class="influence-delta" aria-hidden="true"></span>
        <span class="influence-label">33%</span>
      </div>
      <div class="box-head">
        <div class="box-title"><span class="dot"></span><span class="seat-name"></span><span class="state">idle</span><button type="button" class="copy" disabled>Copy</button></div>
        <button type="button" class="picker" aria-expanded="false"></button>
      </div>
      <div class="box-main">
        <div class="msgs"></div>
        <div class="seat-panel" hidden></div>
      </div>
      <div class="chip"><span class="meta">No verdict yet.</span></div>`;
    arena.append(el);
    boxes[id] = {
      el,
      msgs: el.querySelector(".msgs"),
      state: el.querySelector(".state"),
      chip: el.querySelector(".chip"),
      name: el.querySelector(".seat-name"),
      picker: el.querySelector(".picker"),
      copy: el.querySelector(".copy"),
      panel: el.querySelector(".seat-panel"),
      bar: el.querySelector(".influence"),
      fill: el.querySelector(".influence-fill"),
      share: el.querySelector(".influence-label"),
      delta: el.querySelector(".influence-delta"),
    };
    boxes[id].picker.addEventListener("click", () => togglePanel(id));
    boxes[id].copy.addEventListener("click", () => copyText(boxes[id].copy, sessionColumn(id, exportData())));
  }
  for (const th of document.querySelectorAll("#board th[data-seat]")) th.style.setProperty("--c", `var(--${th.dataset.seat})`);
  refreshSeats();
  refreshInfluence();
}

function refreshSeats() {
  for (const id of IDS) {
    const m = modelOf(id);
    boxes[id].name.textContent = m.name;
    boxes[id].copy.setAttribute("aria-label", `Copy ${m.name}'s chat`);
    boxes[id].picker.innerHTML = `<span class="seat-label">${escapeHtml(SEATS[id].label)}</span><span class="sep"> · </span><code>${escapeHtml(m.id)}</code> <span class="price">${price(m.price)}</span> <span class="caret">▾</span>`;
  }
  for (const th of document.querySelectorAll("#board th[data-seat]")) th.textContent = nameOf(th.dataset.seat);
  if (!chat.isSample()) chat.members(seatNames()); // a sample brawl keeps its own models
}

// Shows a finished brawl in the empty chat to a visitor who hasn't signed in
// (src/sample.js), so they see what they'd get before being asked to sign in.
async function showSample() {
  if (account.signedIn || run) return;
  const md = await fetch("/samples/sample.md").then((r) => (r.ok ? r.text() : null)).catch(() => null);
  const sample = md && parseSample(md);
  if (!sample || account.signedIn || run) return;
  drawSample(chat, sample, (text) => renderBody(text));
}

function refreshAccount() {
  $("#gate").hidden = account.signedIn;
  $("#account").hidden = !account.signedIn;
  $("#google").classList.toggle("off", !site.signin);
  $("#google").setAttribute("aria-disabled", String(!site.signin));
  $("#gate-note").textContent = !site.signin
    ? "Sign-in isn't set up on this server yet."
    : site.lock
      ? `Ask a question: three AIs debate it, vote, and settle on one answer. New accounts get ${money(site.grant)} of free credit for ${STARTER_NAMES}, with web search; the other models unlock when you add credit.`
      : `Ask a question: three AIs debate it, vote, and settle on one answer. New accounts get ${money(site.grant)} of free credit, with every model and web search.`;
  $("#acct-status").innerHTML = account.signedIn
    ? `<span class="email">${escapeHtml(account.email)}</span><span class="sep"> · </span><span class="credit">credit <b class="${account.balance <= 0 ? "low" : ""}">${money(account.balance)}</b></span>`
    : "";
  $("#margin-note").textContent = site.margin ? ` plus ${site.margin}%` : "";
  $("#web-wrap").hidden = !site.search;
  const off = searchOff();
  $("#web").disabled = Boolean(off);
  $("#web").checked = searching();
  $("#web-lock").textContent = `(${off})`;
  $("#web-lock").hidden = !off;
  $("#web-wrap").title = off
    ? `Web search is off: ${off}.`
    : `One web search per question, before the debate: all three models get the same results. ${turnMoney(site.searchCost)} a search, plus a fraction of a cent for the model that writes its query. Up to ${site.searchesPerDay} searches a day.`;
  refreshStartButtons();
  $("#start").title = running() ? "" : account.signedIn ? (account.balance > 0 ? "" : "Add credit to start") : "Sign in with Google to start";
  refreshSeats(); // a lock may have come or gone
  renderJudge();
}

// Life bars: everyone starts full; the leader stays full and the others drain
// toward their share of the influence. The label is the share itself. After a
// vote (`moved`), each bar that changed flashes and shows by how much, ▲ or ▼.
function refreshInfluence(moved = false) {
  const { shares } = influence(run?.transcript ?? [], seatNames());
  const top = Math.max(...IDS.map((id) => shares[id]));
  const leaders = IDS.filter((id) => shares[id] === top);
  for (const id of IDS) {
    const b = boxes[id];
    const pct = Math.round(shares[id] * 100);
    b.fill.style.width = `${(shares[id] / top) * 100}%`;
    const leading = leaders.length === 1 && leaders[0] === id;
    b.bar.classList.toggle("leading", leading);
    b.share.textContent = leading ? `${pct}% · leading` : `${pct}%`;
    const was = Number(b.bar.dataset.share);
    b.bar.dataset.share = String(pct);
    if (!moved || pct === was) continue;
    const up = pct > was;
    b.delta.textContent = `${up ? "▲" : "▼"} ${Math.abs(pct - was)}`;
    b.bar.classList.remove("up", "down");
    void b.bar.offsetWidth; // restart the flash if the last one is still running
    b.bar.classList.add(up ? "up" : "down");
  }
}

// A vote for another model flies as a star, in the voter's colour, from its own
// life bar to the backed model's, arcing over the bars so it shows against the
// sky; the bars move when it lands. Then `land()`.
const STAR_MS = 1100;
function flyVote(from, to, land) {
  const a = boxes[from].bar.querySelector(".influence-track").getBoundingClientRect();
  const b = boxes[to].fill.getBoundingClientRect();
  if (reducedMotion || !a.width || !b.height) return land();
  const x = a.left + a.width / 2;
  const y = a.top + a.height / 2;
  const dx = b.right - x;
  const dy = b.top + b.height / 2 - y;
  const lift = Math.min(90, Math.hypot(dx, dy) * 0.3);
  // A quadratic curve through a control point above the midpoint, sampled into
  // keyframes, with the star turned along it and speeding up as it falls in.
  const frames = [];
  for (let i = 0; i <= 24; i++) {
    const t = (i / 24) ** 1.6;
    const px = dx * t;
    const py = dy * t - 4 * lift * t * (1 - t);
    const angle = Math.atan2(dy - 4 * lift * (1 - 2 * t), dx);
    frames.push({ transform: `translate(${px}px, ${py}px) rotate(${angle}rad) scale(${0.4 + 0.6 * Math.min(1, t * 3)})`, opacity: Math.min(1, i / 3) });
  }
  const star = document.createElement("div");
  star.className = "vote-star";
  star.style.cssText = `left:${x}px;top:${y}px;--c:var(--${from})`;
  document.body.append(star);
  const flight = star.animate(frames, { duration: STAR_MS });
  const done = () => {
    star.remove();
    land();
  };
  flight.onfinish = done;
  flight.oncancel = done;
}

function togglePanel(id, open) {
  const show = open ?? boxes[id].panel.hidden;
  for (const other of IDS) {
    boxes[other].panel.hidden = true;
    boxes[other].picker.setAttribute("aria-expanded", "false");
  }
  if (!show) return;
  renderPanel(id);
  boxes[id].panel.hidden = false;
  boxes[id].picker.setAttribute("aria-expanded", "true");
  boxes[id].panel.querySelector("input:checked")?.focus();
}

function closePanel(id) {
  togglePanel(id, false);
  boxes[id].picker.focus();
}

function renderPanel(id) {
  const b = boxes[id];
  const current = modelOf(id).id;
  const models = SEATS[id].models
    .map((m) => {
      const locked = isLocked(m);
      return `
    <label class="opt${locked ? " locked" : ""}">
      <input type="radio" name="model-${id}" value="${m.id}" ${m.id === current ? "checked" : ""} ${locked ? "disabled" : ""} />
      <span><b>${escapeHtml(m.name)}</b> <span class="price">${price(m.price)}</span><span class="note">${escapeHtml(locked ? "Unlocks when you add credit" : m.note)}</span></span>
    </label>`;
    })
    .join("");
  b.panel.innerHTML = `
    <h3>${escapeHtml(SEATS[id].label)} seat</h3>
    <fieldset class="models">
      <legend>Model <span class="note">list price per million tokens, input / output${site.margin ? `, plus ${site.margin}%` : ""}</span></legend>
      ${models}
    </fieldset>
    <p class="note">Changes apply from the next turn.</p>
    <div class="panel-actions"><button type="button" class="done">Done</button></div>`;
  for (const r of b.panel.querySelectorAll(`input[name="model-${id}"]`)) {
    r.addEventListener("change", () => {
      choices = { ...choices, [id]: r.value };
      settingsStore.save(choices);
      refreshSeats();
    });
    // A pick with the pointer closes the menu; arrow keys only move the choice (their
    // clicks have no detail), so the keyboard still closes it with Done or Escape.
    r.closest("label").addEventListener("click", (e) => {
      if (e.detail > 0 && !r.disabled) setTimeout(() => !b.panel.hidden && closePanel(id));
    });
  }
  b.panel.querySelector(".done").addEventListener("click", () => closePanel(id));
  b.panel.addEventListener("keydown", (e) => e.key === "Escape" && closePanel(id));
}

function setState(id, state) {
  boxes[id].state.textContent = state;
  boxes[id].el.classList.toggle("active", state === "speaking" || state === "thinking");
  cosmos.speaking(id, state === "speaking" || state === "thinking");
  chat.typing(id, state === "speaking" || state === "thinking");
}

// Keeps a column scrolled to its newest text, unless the reader scrolled up.
function follow(container, update) {
  const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 40;
  update();
  if (atBottom) container.scrollTop = container.scrollHeight;
}

function addMessage(container, { label, cls = "", color }) {
  const el = document.createElement("div");
  el.className = `msg ${cls}`;
  if (color) el.style.setProperty("--c", color);
  el.innerHTML = `<div class="msg-label">${escapeHtml(label)}</div><div class="msg-body"></div>`;
  follow(container, () => container.append(el));
  return el;
}

// A note every box shows: the question, a moderator note, the judge. In the group
// chat it's said once: yours as your own message, the rest as centred lines.
function addNote(label, text, cls) {
  for (const id of IDS) {
    const m = addMessage(boxes[id].msgs, { label, cls });
    m.querySelector(".msg-body").textContent = text;
  }
  if (cls === "mod" && label.startsWith("You")) chat.you(text, label.replace(/^You · /, ""));
  else if (cls === "judge") chat.judge(text);
  else if (cls.startsWith("web")) chat.system(`${label}: ${text}`, { kind: cls });
  else chat.system(text, { kind: "note" });
}

// Calls the round across the arena for a moment. The status line already says it
// for screen readers, so the banner is decoration only. It animates only opacity and
// transform, which the compositor runs off the main thread: the columns re-render their
// streaming text every frame, and an animation that needs layout (it used to widen the
// letter spacing) stuttered against them.
const BANNER = reducedMotion
  ? [{ opacity: 0 }, { opacity: 1, offset: 0.15 }, { opacity: 1, offset: 0.7 }, { opacity: 0 }]
  : [
      { opacity: 0, transform: "scale(1.3)" },
      { opacity: 1, transform: "scale(1)", offset: 0.18 },
      { opacity: 1, transform: "scale(1)", offset: 0.7 },
      { opacity: 0, transform: "scale(0.97)" },
    ];
function announceRound(text) {
  const el = $("#round-banner");
  el.textContent = text;
  for (const anim of el.getAnimations()) anim.cancel(); // rounds can come quickly
  el.animate(BANNER, { duration: 1600, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" });
}

function addSeparator(text) {
  for (const id of IDS) {
    const el = document.createElement("div");
    el.className = "round-sep";
    el.textContent = text;
    boxes[id].msgs.append(el);
  }
  chat.system(text, { kind: "round" });
}

function updateChip(id, tags) {
  const parts = [];
  if (tags.endorse) parts.push(`backs ${nameOf(tags.endorse)}`);
  if (tags.confidence != null) parts.push(`confidence ${tags.confidence}`);
  boxes[id].chip.innerHTML = tags.verdict
    ? `<b>Verdict:</b> ${escapeHtml(tags.verdict)}<div class="meta">${escapeHtml(parts.join(" · "))}</div>`
    : `<span class="meta">No verdict tag in last turn.</span>`;
}

// ---- the vote board ---------------------------------------------------------
// One row per debate round: whom each model backed (and with what confidence),
// then the round's result, the judge's call included.
// A follow-up question starts its own rounds under a heading row; `q` is the
// question's number in the session.
const board = {
  rows: new Map(),
  data: [], // [{ question, round, votes: { SEAT: { endorse, confidence, switched } }, result }], for the export
  q: 1,
  reset() {
    this.rows.clear();
    this.data = [];
    this.q = 1;
    $("#board tbody").innerHTML = "";
    $("#board").hidden = true;
  },
  // A heading row for question `n`. The first follow-up also heads the first
  // question's rows, which until then had none: `first` puts that row on top.
  question(n, text, { first = false } = {}) {
    if (!first) this.q = n;
    const tr = document.createElement("tr");
    tr.className = "q-head";
    tr.innerHTML = `<th scope="rowgroup" colspan="${IDS.length + 2}"></th>`;
    tr.firstElementChild.textContent = `Question ${n}: ${text.split("\n")[0].slice(0, 120)}`;
    if (first) $("#board tbody").prepend(tr);
    else $("#board tbody").append(tr);
  },
  entry(round) {
    return this.data.find((r) => r.question === this.q && r.round === round);
  },
  // After a question's rounds, its final answer, folded: every answer of the session
  // stays one click away on the board, in order.
  answer(meta, speaker, text) {
    const tr = document.createElement("tr");
    tr.className = "answer-row";
    tr.dataset.question = String(this.q);
    tr.innerHTML = `<td colspan="${IDS.length + 2}"><details><summary><span class="vote"></span> <span class="meta"></span></summary><div class="msg-body"></div></details></td>`;
    const name = tr.querySelector(".vote");
    name.textContent = `Final answer by ${nameOf(speaker)}`;
    name.style.setProperty("--c", `var(--${speaker})`);
    tr.querySelector("summary .meta").textContent = meta;
    tr.querySelector(".msg-body").innerHTML = renderBody(text, { cite: citations() });
    $("#board tbody").append(tr);
    $("#board").hidden = false;
  },
  row(round) {
    const key = `${this.q}:${round}`;
    if (!this.rows.has(key)) {
      const tr = document.createElement("tr");
      tr.dataset.round = String(round);
      tr.dataset.question = String(this.q);
      tr.innerHTML = `<th scope="row">${round}</th>${IDS.map((id) => `<td data-seat="${id}"><span class="meta">…</span></td>`).join("")}<td class="result"><span class="meta">voting</span></td>`;
      $("#board tbody").append(tr);
      this.rows.set(key, tr);
      this.data.push({ question: this.q, round, votes: {}, result: "" });
      $("#board").hidden = false;
    }
    return this.rows.get(key);
  },
  vote(round, id, tags, previous) {
    const td = this.row(round).querySelector(`td[data-seat="${id}"]`);
    this.entry(round).votes[id] = { endorse: tags?.endorse ?? null, confidence: tags?.confidence ?? null, switched: Boolean(tags?.endorse && previous && previous !== tags.endorse) };
    if (!tags?.endorse) {
      td.innerHTML = `<span class="meta">no vote</span>`;
      return;
    }
    const self = tags.endorse === id;
    const switched = previous && previous !== tags.endorse;
    td.innerHTML = `<span class="vote">${escapeHtml(nameOf(tags.endorse))}</span>${self ? ' <span class="meta">(self)</span>' : ""}${tags.confidence != null ? ` <span class="conf" title="Confidence: how sure this model says it is of its own verdict, 0–100%">${tags.confidence}% sure</span>` : ""}${switched ? ' <span class="switched" title="Switched sides this round">↻</span>' : ""}`;
    td.querySelector(".vote").style.setProperty("--c", `var(--${tags.endorse})`);
    td.dataset.endorse = tags.endorse;
  },
  // `seat`: the model the result names, shown in its seat's colour.
  result(round, text, kind, seat = null) {
    const td = this.row(round).querySelector(".result");
    const name = seat && nameOf(seat);
    const at = name ? text.lastIndexOf(name) : -1;
    if (at < 0) td.textContent = text;
    else {
      td.innerHTML = `${escapeHtml(text.slice(0, at))}<span class="vote">${escapeHtml(name)}</span>${escapeHtml(text.slice(at + name.length))}`;
      td.querySelector(".vote").style.setProperty("--c", `var(--${seat})`);
    }
    td.dataset.kind = kind;
    this.entry(round).result = text;
    chatPoll(this.q, round, this.entry(round), kind);
  },
};

// A round's votes as a poll in the group chat: each model in play, the votes behind
// it and who cast them.
function chatPoll(q, round, entry, kind) {
  const seats = run ? inPlay() : IDS;
  const cast = seats.filter((id) => entry.votes[id]?.endorse);
  const options = seats.map((id) => {
    const voters = cast.filter((v) => entry.votes[v].endorse === id).map((v) => (v === id ? `${nameOf(v)} (itself)` : nameOf(v)));
    return { id, name: nameOf(id), votes: voters.length, voters };
  });
  chat.poll(`${q}:${round}`, { title: `Round ${round} vote: whose answer is best?`, options, total: cast.length, result: entry.result, kind });
}

// ---- turn execution --------------------------------------------------------
async function post(path, body, signal) {
  const res = await fetch(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body), signal });
  if (res.ok) return res;
  const err = await res.json().catch(() => ({}));
  if (Number.isFinite(err.balance_micro)) setBalance(err.balance_micro);
  if (res.status === 401) setSignedOut();
  const kind = err.kind ?? (res.status === 402 ? "credits" : res.status === 401 ? "auth" : "other");
  throw new TurnError(kind, err.error ?? `Server error ${res.status}`);
}

// Streams one turn from the Worker's server-sent events. `onMeta` gets the cost
// and balance the server reports when the turn ends.
async function* turnStream(body, signal, onMeta) {
  const res = await post("/api/turn", body, signal);
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += value;
    let i;
    while ((i = buf.indexOf("\n\n")) >= 0) {
      const line = buf.slice(0, i);
      buf = buf.slice(i + 2);
      if (!line.startsWith("data: ")) continue;
      const ev = JSON.parse(line.slice(6));
      if (ev.type === "delta") yield ev.text;
      else if (ev.type === "done" || ev.type === "thinking") onMeta(ev);
      else if (ev.type === "error") {
        onMeta(ev);
        throw new TurnError(ev.kind ?? "other", ev.message);
      }
    }
  }
}

function setBalance(micro) {
  account.balance = micro;
  refreshAccount();
}

// Runs one model turn. Debate turns render into the speaker's box; the final
// answer renders into the verdict panel. Fixable failures (signed out, out of
// credit, model unavailable) pause the brawl, then the turn is retried.
async function takeTurn(id, phase, round, getTranscript) {
  const isFinal = phase === "final";
  const label = phase === "opening" ? "Opening (blind)" : `Round ${round}`;
  const container = isFinal ? $("#verdict") : boxes[id].msgs;
  for (;;) {
    const msg = isFinal ? container : addMessage(container, { label: `${nameOf(id)} · ${label}`, color: `var(--${id})` });
    const bodyEl = isFinal ? $("#verdict-body") : msg.querySelector(".msg-body");
    const bubble = chat.turn(id, { label: isFinal ? "Final answer" : label, final: isFinal });
    // Draws the turn in its column (or the verdict panel) and in the group chat alike.
    const paint = (html) => {
      follow(container, () => (bodyEl.innerHTML = html));
      bubble.paint(html);
    };
    msg.classList.remove("err");
    msg.classList.add("typing");
    bubble.state("typing", true);
    bodyEl.textContent = "";
    setState(id, "thinking");
    const abort = new AbortController();
    run.aborts.add(abort);
    let text = "";
    const cite = citations();
    const pacer = createPacer((shown) => paint(renderBody(shown, { streaming: true, cite })), {
      instant: instantText || writeSpeed === "instant",
      rate: () => CHARS_PER_SECOND * (SPEED_FACTOR[writeSpeed] ?? 1),
      hidden: () => document.hidden,
      measure: (raw, ended) => visibleText(raw, { streaming: !ended }),
    });
    run.pacers.add(pacer);
    // A tap on a message still being written shows it all, for readers faster than the pace.
    const skip = () => pacer.skip();
    msg.addEventListener("click", skip);
    bubble.el.addEventListener("click", skip);
    // Until its first word, a model is thinking: a pulsing "Thinking…" with the seconds
    // since it was asked. It shows at once when the stream says the model is reasoning,
    // and otherwise after a moment, so a quick answer doesn't blink it on and off. The
    // note is drawn once and only its seconds change, so the pulse runs undisturbed.
    const askedAt = Date.now();
    let thinkingTimer = 0;
    const secondsIn = () => `${Math.round((Date.now() - askedAt) / 1000)}s`;
    const startThinking = () => {
      if (thinkingTimer || text) return;
      paint(`<span class="thinking-note">Thinking… <span class="secs">${secondsIn()}</span></span>`);
      const secs = [bodyEl, bubble.el].map((el) => el.querySelector(".secs"));
      thinkingTimer = setInterval(() => secs.forEach((s) => (s.textContent = secondsIn())), 1000);
    };
    const thinkingSoon = setTimeout(startThinking, 800);
    const stopThinking = () => {
      clearTimeout(thinkingSoon);
      clearInterval(thinkingTimer);
      thinkingTimer = 0;
    };
    const body = { id, phase, round, maxRounds: run.maxRounds, brawl: run.brawl, question: run.question, transcript: getTranscript(), sources: run.sources, earlier: run.earlier, models: modelIds(), length: replyLength };
    const onMeta = (ev) => {
      if (ev.type === "thinking") return startThinking();
      if (Number.isFinite(ev.balance_micro)) setBalance(ev.balance_micro);
      if (ev.cost_micro > 0 && !isFinal) {
        const c = document.createElement("span");
        c.className = "turn-cost";
        c.textContent = `· ${turnMoney(ev.cost_micro)}`;
        msg.querySelector(".msg-label").append(c);
        bubble.note(c.textContent);
      }
    };
    try {
      for await (const chunk of turnStream(body, abort.signal, onMeta)) {
        if (!text) {
          stopThinking();
          setState(id, "speaking");
        }
        text += chunk;
        pacer.push(chunk);
      }
      await pacer.drain();
      paint(renderBody(text, { cite }));
      if (run.stopped) throw new Error(STOPPED);
      setState(id, "done");
      if (isFinal) run.finalBubble = bubble.el;
      else latestBubble[id] = bubble;
      return { speaker: id, phase, round, text };
    } catch (err) {
      pacer.stop();
      // Now, not in `finally`: a fixable error waits for Resume below, and a "Thinking…"
      // still due would cover the error it shows meanwhile.
      stopThinking();
      msg.classList.remove("typing");
      bubble.state("typing", false);
      if (run.stopped) throw new Error(STOPPED);
      setState(id, "error");
      msg.classList.add("err");
      bubble.state("err", true);
      bodyEl.textContent = `${text ? `${text}\n\n` : ""}⚠ ${err.message}`;
      bubble.plain(bodyEl.textContent);
      if (!(err instanceof TurnError) || !FIXABLE.has(err.kind)) return null;
      if (err.kind === "credits") {
        // The dialog opens with the reason in it, so it never asks for credit unexplained.
        pauseRun(`${err.message} Add credit, then press Resume to carry on.`, { alert: true });
        openCredit(err.message);
      } else if (err.kind === "locked") {
        pauseRun(`${err.message} Add credit or pick another model, then press Resume.`, { alert: true });
        openCredit(err.message);
      } else if (err.kind === "rate") {
        pauseRun(`${err.message} Press Resume to try again.`, { alert: true });
      } else if (err.kind === "auth") {
        pauseRun("You were signed out. Sign in again in another tab, then press Resume.", { alert: true });
      } else {
        pauseRun(`${nameOf(id)} isn't available. Pick another model for that seat, then press Resume.`, { alert: true });
        togglePanel(id, true);
      }
      await checkpoint();
      if (!isFinal) msg.remove();
      bubble.remove();
    } finally {
      msg.classList.remove("typing");
      bubble.state("typing", false);
      stopThinking();
      run.aborts.delete(abort);
      run.pacers.delete(pacer);
      msg.removeEventListener("click", skip);
      bubble.el.removeEventListener("click", skip);
    }
  }
}

// Asks the judge whether a unanimous vote is real agreement. A judge that can't
// run lets the vote stand.
// `tick(seconds)` is called every second while it thinks, so the screen can say how long.
async function askJudge(verdicts, tick = () => {}, { pick = false } = {}) {
  const abort = new AbortController();
  run.aborts.add(abort);
  const since = Date.now();
  const timer = setInterval(() => tick(Math.round((Date.now() - since) / 1000)), 1000);
  try {
    const seats = inPlay().filter((id) => id in verdicts);
    const res = await post("/api/judge", { brawl: run.brawl, question: run.question, verdicts, models: modelIds(), seats, judge: judgeModel().id, ...(pick ? { pick } : {}) }, abort.signal);
    const j = await res.json();
    if (Number.isFinite(j.balance_micro)) setBalance(j.balance_micro);
    return j;
  } catch (err) {
    if (run.stopped) throw new Error(STOPPED);
    return { agree: true, pick: null, judged: false, reason: err.message };
  } finally {
    clearInterval(timer);
    run.aborts.delete(abort);
  }
}

// One web search for question `number`, before its debate (worker/search.js). The
// results go in every turn of the question and are listed above the chats. A search
// that can't run says why in every column, and the brawl goes on without sources.
async function searchWeb(number) {
  setStatus("Searching the web…", { busy: true });
  // While it runs, every column shows it pulsing with the seconds, like a model thinking.
  const since = Date.now();
  const waiting = IDS.map((id) => {
    const m = addMessage(boxes[id].msgs, { label: "Web search", cls: "web" });
    m.querySelector(".msg-body").innerHTML = `<span class="thinking-note">Searching the web… <span class="secs">0s</span></span>`;
    return m;
  });
  waiting.push(chat.system("", { kind: "web", html: `<span class="thinking-note">Searching the web… <span class="secs">0s</span></span>` }));
  const ticker = setInterval(() => {
    for (const m of waiting) m.querySelector(".secs").textContent = `${Math.round((Date.now() - since) / 1000)}s`;
  }, 1000);
  const abort = new AbortController();
  run.aborts.add(abort);
  let res;
  try {
    res = await (await post("/api/search", { brawl: run.brawl, question: run.question, earlier: run.earlier.map((e) => e.question) }, abort.signal)).json();
  } catch (err) {
    if (run.stopped) throw new Error(STOPPED);
    res = { message: `The web search failed (${err.message}), so this question runs without web sources.` };
  } finally {
    clearInterval(ticker);
    for (const m of waiting) m.remove();
    run.aborts.delete(abort);
  }
  if (run.stopped) throw new Error(STOPPED);
  if (Number.isFinite(res.balance_micro)) setBalance(res.balance_micro);
  const label = `Web search${res.cost_micro > 0 ? ` · ${turnMoney(res.cost_micro)}` : ""}`;
  if (!res.sources) {
    searchSpent = { quota: "month", daily: "day" }[res.skipped] ?? searchSpent;
    refreshAccount(); // greys out "Search the web" when the month or the day is used up
    addNote(label, res.message, "web off");
    // Above the chats too, where it stays after the status moves on.
    const p = document.createElement("p");
    p.className = "source-skip";
    p.textContent = `${number > 1 ? `Question ${number}: ` : ""}${res.message}`;
    $("#sources-list").append(p);
    $("#sources").hidden = false;
    return setStatus(res.message);
  }
  run.sources = res.sources;
  showSources(number, res.sources, sessionSources().length - res.sources.results.length + 1);
  addNote(label, `Searched for “${res.sources.query}”: ${resultsOf(res.sources.results.length)}, listed above the chats. All three models see the same ones.`, "web");
}

const resultsOf = (n) => `${n} web result${n === 1 ? "" : "s"}`;

// A question's web sources, numbered as the models cite them, folded under the status.
function showSources(number, { query, date, results }, first) {
  const d = document.createElement("details");
  d.className = "source-list";
  d.innerHTML = `<summary></summary><ol start="${first}"></ol>`;
  d.querySelector("summary").textContent = `${number > 1 ? `Question ${number}: ` : ""}${resultsOf(results.length)} for “${query}”, ${date}`;
  for (const r of results) {
    const li = document.createElement("li");
    const a = document.createElement("a");
    if (/^https?:\/\//i.test(r.url)) a.href = r.url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.textContent = r.title;
    const where = document.createElement("span");
    where.className = "meta";
    where.textContent = ` ${URL.canParse(r.url) ? new URL(r.url).hostname : ""}${r.age ? ` · ${r.age}` : ""}`;
    const snippet = document.createElement("div");
    snippet.className = "snippet";
    snippet.textContent = r.snippet;
    li.append(a, where, snippet);
    d.querySelector("ol").append(li);
  }
  $("#sources-list").append(d);
  $("#sources").hidden = false;
  $("#sources .note").hidden = false;
}

function clearSources() {
  $("#sources-list").innerHTML = "";
  $("#sources").hidden = true;
  $("#sources .note").hidden = true; // Brave's credit, once there are results
}

// Moderator messages queue up and enter the transcript before the next turn.
function flushInterjections() {
  while (run.pending.length) {
    const text = run.pending.shift();
    run.transcript.push({ speaker: "MODERATOR", text });
    addNote("You · moderator", text, "mod");
  }
}

function pauseRun(message, opts) {
  run.paused = true;
  $("#pause").textContent = "Resume";
  setStatus(message, opts);
}

async function checkpoint() {
  while (run.paused && !run.stopped) await sleep(150);
  if (run.stopped) throw new Error(STOPPED);
  flushInterjections();
}

// The seat that opens round 1, drawn for each brawl. The browser tests pin it in
// sessionStorage so their scripted rounds run in a known order.
function firstSeat() {
  try {
    const pinned = window.sessionStorage.getItem("chat-brawlers.first");
    if (IDS.includes(pinned)) return pinned;
  } catch {
    // Blocked storage: draw.
  }
  return IDS[Math.floor(Math.random() * IDS.length)];
}

// The seats still in the debate: a seat whose model keeps failing can be taken out.
const inPlay = () => IDS.filter((id) => !run.out.has(id));
const ALL_FAILED = "Chat Brawlers couldn't reach its models, so the brawl stopped. Failed turns that produced nothing cost nothing. Try again in a few minutes.";
const TOO_FEW = "Only one model is left, so there is no one to debate. Pick other models and start again.";

async function brawl() {
  const endorsements = {}; // each model's latest vote
  const verdicts = {}; // each model's latest verdict
  const confidence = {}; // each model's latest confidence

  // After a turn: a model that failed twice in a row pauses the brawl so the
  // moderator can pick another model for its seat, or carry on without it.
  const afterTurn = async (id, turn) => {
    if (turn) return void (run.fails[id] = 0);
    run.fails[id] = (run.fails[id] ?? 0) + 1;
    if (run.fails[id] < 2) return;
    const failing = modelOf(id).id;
    pauseRun(`${nameOf(id)} failed twice in a row. Pick another model for that seat, or press Resume to carry on without it.`, { alert: true });
    togglePanel(id, true);
    await checkpoint();
    run.fails[id] = 0;
    if (modelOf(id).id !== failing) return;
    run.out.add(id);
    delete endorsements[id];
    setState(id, "out");
    const note = `${nameOf(id)} is unavailable and has left the debate. Carry on without it.`;
    run.transcript.push({ speaker: "MODERATOR", text: note });
    addNote("Moderator", note, "mod");
  };
  const record = (turn) => {
    if (!turn) return null;
    run.transcript.push(turn);
    const tags = parseTags(turn.text, seatNames()); // a vote by model name counts too
    updateChip(turn.speaker, tags);
    if (tags.verdict) verdicts[turn.speaker] = tags.verdict;
    if (tags.confidence != null) confidence[turn.speaker] = tags.confidence;
    // In the chat: the confidence on the turn, and the vote as a star on the message it backs.
    if (tags.confidence != null) latestBubble[turn.speaker]?.confidence(tags.confidence);
    if (turn.phase === "debate" && IDS.includes(tags.endorse)) latestBubble[tags.endorse]?.vote(turn.speaker);
    const backs = turn.phase === "debate" && IDS.includes(tags.endorse) && tags.endorse !== turn.speaker;
    if (backs) flyVote(turn.speaker, tags.endorse, () => refreshInfluence(true));
    else refreshInfluence(true);
    refreshSaving();
    return tags;
  };

  if (run.mode === "blind") {
    await checkpoint();
    setStatus("Opening round: all three answer at once, without seeing each other.");
    addSeparator("Opening · blind");
    const snapshot = [...run.transcript];
    const turns = await Promise.all(IDS.map((id) => takeTurn(id, "opening", 0, () => snapshot)));
    turns.forEach(record);
    if (turns.every((t) => !t)) return setStatus(ALL_FAILED);
    for (const [i, id] of IDS.entries()) await afterTurn(id, turns[i]);
    if (inPlay().length < 2) return setStatus(TOO_FEW);
  }

  let result = null;
  let judged = null; // the judge's last call on a unanimous vote
  let sameAnswer = false; // the votes split, but the judge found every verdict gives the same answer
  for (let r = 1; r <= run.maxRounds; r++) {
    await checkpoint();
    addSeparator(`Round ${r}`);
    announceRound(`Round ${r}`);
    board.row(r);
    let spoke = 0;
    let failed = 0;
    for (const id of speakingOrder(r, run.first)) {
      if (run.out.has(id)) continue;
      await checkpoint();
      setStatus(`Round ${r} of ${run.maxRounds}: ${nameOf(id)} has the floor.`);
      const turn = await takeTurn(id, "debate", r, () => [...run.transcript]);
      const tags = record(turn);
      board.vote(r, id, tags, endorsements[id]);
      if (tags?.endorse && endorsements[id] && tags.endorse !== endorsements[id]) {
        chat.system(`${nameOf(id)} now backs ${tags.endorse === id ? "itself" : nameOf(tags.endorse)}`, { kind: "switch" });
      }
      if (tags?.endorse) endorsements[id] = tags.endorse;
      spoke++;
      if (!turn) failed++;
      // Every seat failing in one round means the service is down, not one model.
      if (failed === spoke && spoke === inPlay().length) return setStatus(ALL_FAILED);
      await afterTurn(id, turn);
      if (inPlay().length < 2) return setStatus(TOO_FEW);
    }
    result = tally(endorsements, inPlay());
    if (result.status !== "consensus") {
      const split = result.status === "majority" ? `${result.counts[result.spokesperson]} of ${inPlay().length} back ${nameOf(result.spokesperson)}` : "Split";
      // Models that agree often each back their own wording, which splits the vote
      // with nothing left to argue: ask the judge whether the verdicts give the
      // same answer anyway. Only a judge that actually ruled can end the debate.
      if (inPlay().every((id) => verdicts[id])) {
        await checkpoint();
        board.result(r, `${split} · judge checking…`, "judging");
        setStatus("The votes are split. The judge is checking whether the verdicts give the same answer anyway.");
        const check = await askJudge(Object.fromEntries(inPlay().map((id) => [id, verdicts[id]])), (s) => board.result(r, `${split} · judge checking… ${s}s`, "judging"));
        if (check.judged && check.agree) {
          // The most-backed model speaks, whoever it backed itself; on a tie, the most
          // confident. The label says which, so a speaker that voted for another reads right.
          const byConfidence = [...inPlay()].sort((a, b) => (confidence[b] ?? 0) - (confidence[a] ?? 0));
          const speaker = result.spokesperson ?? byConfidence[0];
          const backers = inPlay().filter((id) => id !== speaker && endorsements[id] === speaker).map(nameOf);
          if (endorsements[speaker] === speaker) backers.push("itself");
          const named = backers.length > 1 ? `${backers.slice(0, -1).join(", ")} and ${backers.at(-1)}` : backers[0];
          const why = result.spokesperson ? `backed by ${named}` : "the most confident, on a tied vote";
          const score = Object.values(result.counts).sort((a, b) => b - a).join("–");
          result = { status: "consensus", spokesperson: speaker, counts: result.counts };
          judged = check;
          sameAnswer = true;
          board.result(r, `Votes split ${score} · judge: same answer from all · ${nameOf(speaker)} speaks (${why})`, "agreed", speaker);
          break;
        }
      }
      board.result(r, split, result.status);
      continue;
    }
    await checkpoint();
    board.result(r, `All back ${nameOf(result.spokesperson)} · judge checking…`, "judging", result.spokesperson);
    setStatus(`${inPlay().length === IDS.length ? "All three back" : "Both models left back"} one verdict. The judge is checking their verdicts really agree.`);
    judged = await askJudge(Object.fromEntries(inPlay().map((id) => [id, verdicts[id] ?? ""])), (s) => board.result(r, `All back ${nameOf(result.spokesperson)} · judge checking… ${s}s`, "judging", result.spokesperson));
    if (judged.agree) {
      board.result(r, `Agreed: ${nameOf(result.spokesperson)}${judged.judged ? " · judge confirms" : ""}`, "agreed", result.spokesperson);
      break;
    }
    board.result(r, `Judge: verdicts differ. ${judged.reason}`, "differ");
    const note = `${inPlay().length === IDS.length ? "All three" : "Both of you"} backed ${nameOf(result.spokesperson)}'s verdict, but your own verdicts still differ: ${judged.reason || "they don't give the same answer."} Settle this difference or state it plainly.`;
    run.transcript.push({ speaker: "JUDGE", text: note });
    addNote("Judge", note, "judge");
  }

  await checkpoint();
  result ??= tally(endorsements, inPlay());
  const verdict = $("#verdict");
  verdict.hidden = false;
  $("#verdict-body").innerHTML = "";
  let speaker = result.spokesperson;
  // A seat that left can still be the verdict the others backed; one of them speaks for it.
  if (speaker && run.out.has(speaker)) speaker = inPlay().find((id) => endorsements[id] === speaker) ?? inPlay()[0];
  const counts = Object.entries(result.counts).map(([id, n]) => `${nameOf(id)} ${n}`).join(", ");
  // No majority: the judge picks the verdict that best answers the question, so the
  // brawl still ends on one answer. A judge that can't pick leaves it to the most
  // confident model. The judge's word goes in the transcript, so the speaker knows why.
  let judgePicked = false;
  if (result.status === "tie") {
    const withVerdict = inPlay().filter((id) => verdicts[id]);
    let call = { pick: null };
    if (withVerdict.length >= 2) {
      setStatus("No majority. The judge is picking the strongest answer.", { busy: true });
      call = await askJudge(Object.fromEntries(withVerdict.map((id) => [id, verdicts[id]])), (s) => setStatus(`No majority. The judge is picking the strongest answer… ${s}s`, { busy: true }), { pick: true });
      await checkpoint();
    }
    judgePicked = withVerdict.includes(call.pick);
    speaker = judgePicked ? call.pick : [...inPlay()].sort((a, b) => (confidence[b] ?? 0) - (confidence[a] ?? 0))[0];
    const note = judgePicked
      ? `No majority. The judge picks ${nameOf(speaker)}'s verdict${call.reason ? `: ${call.reason}` : "."}`
      : `No majority, and the judge couldn't pick. ${nameOf(speaker)}, the most confident, gives the final answer.`;
    run.transcript.push({ speaker: "JUDGE", text: note });
    addNote("Judge", note, "judge");
  }
  const how = {
    consensus: sameAnswer ? "Same answer from all (votes split)" : judged && !judged.agree ? "Unanimous vote, but the judge saw differences" : "Unanimous",
    majority: `Majority (${result.counts[result.spokesperson]} of ${inPlay().length})`,
    tie: judgePicked ? "Judge's call (votes split)" : "Most confident (votes split)",
  }[result.status];
  $("#verdict-meta").textContent = `${how} · spoken by ${nameOf(speaker)}${counts ? ` · votes: ${counts}` : ""}`;
  verdict.style.setProperty("--c", `var(--${speaker})`);
  setStatus(`${nameOf(speaker)} is writing the final answer.`);
  // Notes can't reach the final answer any more.
  run.final = true;
  refreshStartButtons();
  const final = await takeTurn(speaker, "final", 0, () => [...run.transcript]);
  if (final) {
    run.transcript.push(final);
    board.answer($("#verdict-meta").textContent, speaker, final.text);
    chat.pinAnswer(run.finalBubble, `Final answer by ${nameOf(speaker)} · ${how}`);
    refreshSaving();
    cosmos.verdict();
  }
  setStatus("Brawl over.");
}

// ---- copy & export -----------------------------------------------------------
// One entry per question in the session, oldest first; the current one last.
const exportData = () => {
  const common = {
    brawl: run.brawl,
    first: run.first,
    names: Object.fromEntries(IDS.map((id) => [id, nameOf(id)])),
    labels: Object.fromEntries(IDS.map((id) => [id, SEATS[id].label])),
  };
  const current = { question: run.question, transcript: run.transcript, sources: run.sources, verdict: { meta: $("#verdict-meta").textContent } };
  let firstSource = 1;
  return [...run.past, current].map((q, i) => {
    const entry = { ...common, ...q, firstSource, board: board.data.filter((r) => r.question === i + 1) };
    firstSource += q.sources?.results.length ?? 0;
    return entry;
  });
};
const sessionMarkdown = (questions) => questions.map((q) => brawlMarkdown(q)).join("\n---\n\n");
const sessionColumn = (seat, questions) => questions.map((q) => columnText(seat, q)).join("\n\n---\n\n");

// Copy and Export work once there's something to save, even mid-brawl.
function refreshSaving() {
  const has = Boolean(run?.transcript.length || run?.past.length);
  for (const id of IDS) boxes[id].copy.disabled = !has;
  $("#export").disabled = !has;
  const answered = Boolean(run?.transcript.some((t) => t.phase === "final"));
  $("#copy-verdict").disabled = !answered;
  $("#share").hidden = !answered;
}

const copyResets = new WeakMap();
async function copyText(button, text) {
  let ok = true;
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    ok = false;
  }
  const label = copyResets.has(button) ? copyResets.get(button).label : button.textContent;
  button.textContent = ok ? "Copied" : "Couldn't copy";
  clearTimeout(copyResets.get(button)?.timer);
  copyResets.set(button, { label, timer: setTimeout(() => (button.textContent = label), 1500) });
  if (!ok) setStatus("Your browser didn't allow copying. Use Export to save the brawl as a file instead.");
}

$("#copy-verdict").addEventListener("click", (e) => {
  const final = run.transcript.find((t) => t.phase === "final");
  copyText(e.currentTarget, `${$("#verdict-meta").textContent}\n\n${final.text.trim()}`);
});

// Share sends the question, who won and the final answer, with a link that opens the
// site with the question filled in: to the phone's share sheet (a group chat, a
// message, anywhere), or where there's none, to the clipboard. The text comes from
// this tab; nothing is stored.
async function shareBrawl(button) {
  const final = run?.transcript.find((t) => t.phase === "final");
  if (!final) return;
  const meta = $("#verdict-meta").textContent;
  const how = meta.split(" · ")[0];
  const { text, url } = shareText({ question: run.question, winner: nameOf(final.speaker), how, answer: final.text, origin: location.origin });
  // One text with the link at its end, and no separate title or url: given a url, many
  // apps (WhatsApp, Messages, X) share only the link and its preview, which shows the
  // site's name, and drop the text, the brawl itself.
  const data = { text: `${text} ${url}` };
  if (navigator.share && navigator.canShare?.(data) !== false) {
    try {
      await navigator.share(data);
      return;
    } catch (err) {
      if (err?.name === "AbortError") return; // closed the share sheet
    }
  }
  await copyText(button, data.text);
  if (button.textContent === "Copied") setStatus("Copied: paste it into any chat.");
}
$("#share").addEventListener("click", (e) => shareBrawl(e.currentTarget));
$("#chat .share").addEventListener("click", (e) => shareBrawl(e.currentTarget));

$("#export").addEventListener("click", () => {
  const questions = exportData();
  const blob = new Blob([sessionMarkdown(questions)], { type: "text/markdown;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = exportFileName(questions[0].question);
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 0);
});

// ---- controls ------------------------------------------------------------
// The judge menu: Luna 6 unless the user picked another. A judge that needs credit
// is greyed out until the account has added some, and the default judges meanwhile.
let judgePick = judgeFor(settingsStore.loadJudge());
const judgeLocked = (m) => site.lock && !account.unlocked && !judgeFree(m);
const judgeModel = () => (judgeLocked(judgePick) ? JUDGE_STARTER : judgePick);
function renderJudge() {
  $("#judge").innerHTML = JUDGES.map((m) => {
    const note = judgeLocked(m) ? " · unlocks when you add credit" : m === JUDGES[0] ? " (default)" : "";
    return `<option value="${m.id}" ${judgeLocked(m) ? "disabled" : ""}>${escapeHtml(m.name)}${note}</option>`;
  }).join("");
  $("#judge").value = judgeModel().id;
}
renderJudge();
$("#judge").addEventListener("change", () => {
  judgePick = judgeFor($("#judge").value);
  settingsStore.saveJudge(judgePick.id);
});

// "Search the web": on unless turned off, and remembered.
$("#web").addEventListener("change", () => {
  webOn = $("#web").checked;
  settingsStore.saveWeb(webOn);
});

// Example questions fill the box. They don't start a brawl: that spends credit.
for (const b of document.querySelectorAll(".examples button")) {
  b.addEventListener("click", () => {
    $("#question").value = b.dataset.question;
    $("#question").focus();
  });
}

// The composer under the chat does everything, like a messenger's: before a brawl it
// asks the question ("Start brawl"); while one runs it sends a moderator note to the
// next turn ("Send note"), until the final answer starts; after one it asks a follow-up
// of the same three models, who keep everything said so far ("Ask next"). "New brawl"
// clears it all and goes back to the empty start.
const running = () => Boolean(run && !run.done);
const followUp = () => Boolean(run?.done && run.transcript.length);
function refreshStartButtons() {
  const [label, placeholder] = running()
    ? ["Send note", run.final ? "The final answer is being written…" : "Steer the brawl: a note for the next turn…"]
    : followUp()
      ? ["Ask next", "Ask the same three a follow-up question…"]
      : ["Start brawl", "Ask the brawlers a question…"];
  $("#start").textContent = label;
  $("#question").placeholder = placeholder;
  $("#start").disabled = running() ? Boolean(run.final) : !canStart();
  $("#restart").hidden = !run;
  $("#restart").disabled = running();
  $("#ask .examples").hidden = Boolean(run);
}

function setRunning(on) {
  refreshSaving();
  refreshStartButtons();
  $("#pause").disabled = !on;
  $("#stop").disabled = !on;
  $("#pause").textContent = "Pause";
}

// Runs a brawl on the question in the box. `next`: a follow-up in the same session,
// so the columns, the vote board and the brawl id stay, and every turn also carries
// the earlier questions with their whole discussion. Otherwise it starts from scratch.
async function begin({ next = false } = {}) {
  const question = $("#question").value.trim();
  if (!question) return;
  if (!account.signedIn) return setStatus("Sign in with Google to start a brawl.");
  if (account.balance <= 0) {
    setStatus("Your credit has run out. Add credit to start.", { alert: true });
    return openCredit("Your credit has run out. Add credit to start.");
  }
  const prior = next && run?.transcript.length ? run : null;
  if (prior) {
    // The last final answer lived in the verdict panel; it stays in its speaker's column.
    const final = prior.transcript.find((t) => t.phase === "final");
    if (final) {
      const m = addMessage(boxes[final.speaker].msgs, { label: `${nameOf(final.speaker)} · Final answer` });
      m.querySelector(".msg-body").innerHTML = renderBody(final.text, { cite: citations() });
    }
    chat.unpin();
  } else {
    for (const id of IDS) boxes[id].msgs.innerHTML = "";
    board.reset();
    clearSources();
    chat.reset();
    chat.members(seatNames());
    for (const k in latestBubble) delete latestBubble[k];
  }
  for (const id of IDS) {
    boxes[id].chip.innerHTML = `<span class="meta">No verdict yet.</span>`;
    setState(id, "idle");
  }
  togglePanel(IDS[0], false);
  $("#verdict").hidden = true;
  const number = prior ? prior.past.length + 2 : 1;
  run = {
    question,
    mode: $("#mode").value,
    maxRounds: Number($("#rounds").value),
    transcript: [],
    sources: null, // this question's web search, once it ran
    pending: [],
    paused: false,
    stopped: false,
    done: false,
    aborts: new Set(),
    pacers: new Set(), // the turns still being written out
    out: new Set(),
    fails: {},
    first: firstSeat(),
    // Groups the calls at Kottos AI; a follow-up keeps it, being the same conversation.
    brawl: prior?.brawl ?? crypto.randomUUID().replace(/-/g, "").slice(0, 16),
    earlier: prior ? [...prior.earlier, { question: prior.question, transcript: prior.transcript, sources: prior.sources }] : [],
    past: prior ? [...prior.past, { question: prior.question, transcript: prior.transcript, sources: prior.sources, verdict: { meta: $("#verdict-meta").textContent } }] : [],
  };
  refreshInfluence();
  if (prior) {
    addSeparator(`Question ${number}`);
    if (number === 2) board.question(1, prior.question, { first: true });
    board.question(number, question);
  }
  addNote(prior ? `You · question ${number}` : "You · question", question, "mod");
  $("#question").value = "";
  fitComposer();
  setRunning(true);
  try {
    if (searching()) await searchWeb(number);
    await brawl();
  } catch (err) {
    if (err.message !== STOPPED) setStatus(`Error: ${err.message}`);
  } finally {
    for (const id of IDS) if (!["error", "out"].includes(boxes[id].state.textContent)) setState(id, "idle");
    run.done = true;
    setRunning(false);
  }
}

// Back to the empty page: no chats, board or answer, the box empty, nothing sent.
function restart() {
  for (const id of IDS) {
    boxes[id].msgs.innerHTML = "";
    boxes[id].chip.innerHTML = `<span class="meta">No verdict yet.</span>`;
    setState(id, "idle");
  }
  togglePanel(IDS[0], false);
  $("#verdict").hidden = true;
  $("#verdict-body").innerHTML = "";
  $("#verdict-meta").textContent = "";
  board.reset();
  clearSources();
  chat.reset();
  chat.members(seatNames());
  for (const k in latestBubble) delete latestBubble[k];
  for (const anim of $("#round-banner").getAnimations()) anim.cancel();
  run = null;
  $("#question").value = "";
  fitComposer();
  setStatus("Waiting for a question.");
  refreshInfluence();
  setRunning(false);
  $("#question").focus();
}

$("#ask").addEventListener("submit", (e) => {
  e.preventDefault();
  const text = $("#question").value.trim();
  if (!text) return;
  if (running()) {
    if (run.final) return;
    run.pending.push(text);
    $("#question").value = "";
    fitComposer();
    setStatus(`${statusEl.textContent} (Moderator note queued for the next turn.)`);
    return;
  }
  brawling = begin({ next: followUp() });
});
// Enter sends, as in a messenger; Shift+Enter starts a new line.
$("#question").addEventListener("keydown", (e) => {
  if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
    e.preventDefault();
    $("#ask").requestSubmit();
  }
});
// The box grows with what's typed, up to a few lines.
function fitComposer() {
  const q = $("#question");
  q.style.height = "auto";
  q.style.height = `${Math.min(q.scrollHeight + 2, 160)}px`;
}
$("#question").addEventListener("input", fitComposer);
$("#restart").addEventListener("click", () => restart());

$("#pause").addEventListener("click", async () => {
  if (run.paused) {
    if (!account.signedIn) await refreshMe(); // signed in again in another tab
    run.paused = false;
    $("#pause").textContent = "Pause";
    setStatus("Resumed.");
  } else {
    pauseRun("Paused after the current turn. Add a moderator note or change a seat's model, then resume.");
  }
});

// The brawl under way, if any: begin()'s promise, settled once it has wound down.
let brawling = null;

function stopRun() {
  run.stopped = true;
  for (const a of run.aborts) a.abort();
  for (const p of run.pacers) p.stop();
}
$("#stop").addEventListener("click", () => {
  stopRun();
  setStatus("Stopped.");
});


// ---- account ----------------------------------------------------------------
async function getJson(path) {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`Error ${res.status}`);
  return res.json();
}

function setSignedOut() {
  account = { signedIn: false, email: "", balance: 0, unlocked: false };
  refreshAccount();
}

async function refreshMe() {
  const me = await getJson("/api/me").catch(() => ({ signedIn: false }));
  account = { signedIn: Boolean(me.signedIn), email: me.email ?? "", balance: me.balance_micro ?? 0, unlocked: Boolean(me.unlocked) };
  refreshAccount();
}

$("#google").addEventListener("click", (e) => {
  if (!site.signin) e.preventDefault();
});

// Signing out leaves the page as a new visitor finds it: any brawl stopped, the chat,
// board, answer and question box empty.
$("#acct-signout").addEventListener("click", async () => {
  if (running()) {
    stopRun();
    await brawling?.catch(() => {});
  }
  await fetch("/api/auth/logout", { method: "POST" }).catch(() => {});
  setSignedOut();
  restart();
  showSample();
  setDrawer(false);
  setStatus("Signed out.");
  window.scrollTo(0, 0);
});

for (const b of document.querySelectorAll("dialog [data-close]")) b.addEventListener("click", () => b.closest("dialog").close());

// `reason`: why a brawl stopped for credit, shown at the top of the dialog.
function openCredit(reason = "") {
  $("#credit-error").textContent = "";
  $("#credit-reason").textContent = reason;
  $("#credit-reason").hidden = !reason;
  $("#credit").showModal();
}
$("#acct-credit").addEventListener("click", () => openCredit());

$("#credit-form").addEventListener("submit", async (e) => {
  e.preventDefault();
  const cents = Number(new FormData(e.target).get("amount"));
  $("#credit-error").textContent = "";
  try {
    const { url } = await (await post("/api/credit", { cents })).json();
    // Checkout opens in its own tab so a paused debate survives; this tab watches
    // for the credit to arrive. Without a new tab, go there in this one.
    const tab = window.open(url, "_blank");
    if (!tab) return location.assign(url);
    $("#credit").close();
    waitForCredit();
  } catch (err) {
    $("#credit-error").textContent = err.message;
  }
});

// Polls the balance until a payment lands (the webhook usually takes seconds).
let watching = false;
async function waitForCredit(tries = 120, everyMs = 3000) {
  if (watching) return;
  watching = true;
  const before = account.balance;
  setStatus("Waiting for your payment to finish in the other tab…");
  try {
    for (let i = 0; i < tries && account.balance <= before; i++) {
      await sleep(everyMs);
      await refreshMe();
    }
  } finally {
    watching = false;
  }
  if (account.balance > before) {
    setStatus(`Credit added: you have ${money(account.balance)}.${run?.paused ? " Press Resume to carry on." : ""}`);
  } else {
    setStatus("No payment yet. If you finish it later, your credit appears after a reload.");
  }
}

// What a redirect back to the site came to say: from Google sign-in or Stripe.
function arrivalMessage(q) {
  const signedin = q.get("signedin");
  if (signedin === "new") return `Welcome! ${money(account.balance)} of credit is on your account. Ask away.`;
  if (signedin === "new-nogrant") return "Welcome! Add credit to start your first brawl.";
  if (signedin === "back") return "Signed in.";
  if (q.get("signin") === "failed") return "Sign-in didn't go through. Try again.";
  const credit = q.get("credit");
  if (credit === "cancelled") return "Payment cancelled. Nothing was charged.";
  if (credit === "added") return "Payment received. If you started it from a debate, go back to that tab: your credit appears there in a moment.";
  return null;
}

// ---- startup -------------------------------------------------------------
const INTRO_SEEN = "chat-brawlers.intro";

async function intro(arrived) {
  let seen = arrived;
  try {
    seen ||= sessionStorage.getItem(INTRO_SEEN) === "1";
    sessionStorage.setItem(INTRO_SEEN, "1");
  } catch {
    // Storage blocked: the intro plays on every visit.
  }
  if (seen) return $("#intro").remove();
  const app = $("#app");
  app.inert = true;
  await playIntro($("#intro"), { reducedMotion });
  app.inert = false;
  $("#question").focus({ preventScroll: true });
}

// Chat (one group thread) or Columns (a column per model), remembered.
function setView(view) {
  document.body.classList.toggle("view-chat", view === "chat");
  document.body.classList.toggle("view-columns", view === "columns");
  for (const b of document.querySelectorAll(".view-switch button")) b.setAttribute("aria-pressed", String(b.dataset.view === view));
}

// Short or long replies, remembered; it applies from the next turn.
function setLength(length) {
  replyLength = length;
  for (const b of document.querySelectorAll("#length button")) b.setAttribute("aria-pressed", String(b.dataset.length === length));
}
for (const b of document.querySelectorAll("#length button")) {
  b.addEventListener("click", () => {
    setLength(b.dataset.length);
    settingsStore.saveLength(b.dataset.length);
  });
}
setLength(replyLength);

// How fast turns are written out, remembered. Faster applies at once, to the turns
// being written too; Instant shows what has arrived of them straight away.
function setSpeed(speed) {
  writeSpeed = speed;
  for (const b of document.querySelectorAll("#speed button")) b.setAttribute("aria-pressed", String(b.dataset.speed === speed));
  if (speed === "instant") for (const p of run?.pacers ?? []) p.skip();
}
for (const b of document.querySelectorAll("#speed button")) {
  b.addEventListener("click", () => {
    setSpeed(b.dataset.speed);
    settingsStore.saveSpeed(b.dataset.speed);
  });
}
setSpeed(writeSpeed);

// On a phone the settings are a drawer over the chat, opened from the top bar. Shut,
// it's inert, so the keyboard and screen readers skip it; on a wide screen it's the
// sidebar and always open.
const narrow = matchMedia("(max-width: 899px)");
function setDrawer(open) {
  const side = $("#side");
  side.classList.toggle("open", open);
  side.inert = narrow.matches && !open;
  $("#side-scrim").hidden = !(narrow.matches && open);
  $("#side-open").setAttribute("aria-expanded", String(open));
  if (open && narrow.matches) $("#side-close").focus();
}
$("#side-open").addEventListener("click", () => setDrawer(true));
$("#side-close").addEventListener("click", () => {
  setDrawer(false);
  $("#side-open").focus();
});
$("#side-scrim").addEventListener("click", () => setDrawer(false));
$("#side").addEventListener("keydown", (e) => e.key === "Escape" && narrow.matches && setDrawer(false));
narrow.addEventListener("change", () => setDrawer(false));
setDrawer(false);
for (const b of document.querySelectorAll(".view-switch button")) {
  b.addEventListener("click", () => {
    setView(b.dataset.view);
    settingsStore.saveView(b.dataset.view);
  });
}
setView(settingsStore.loadView());

buildArena();
const query = new URLSearchParams(location.search);
// A link can carry a question (?q=, from an ad's sitelink): it fills the box and
// starts nothing, since a brawl spends credit. A visitor who clicked an ad (?utm_medium=cpc)
// wants to type at once, so the intro is skipped for them.
const prefill = (query.get("q") ?? "").trim().slice(0, 500);
if (prefill) $("#question").value = prefill;
const fromAd = Boolean(prefill) || query.get("utm_medium") === "cpc";
refreshAccount();
const arrived = ["signedin", "signin", "credit"].some((k) => query.has(k));
if (arrived) history.replaceState(null, "", location.pathname);
intro(arrived || fromAd);
getJson("/api/config")
  .then((cfg) => {
    site = {
      signin: Boolean(cfg.signin),
      grant: cfg.grant_micro ?? 0,
      margin: cfg.margin_percent ?? 0,
      lock: Boolean(cfg.lock_until_topup),
      search: Boolean(cfg.search),
      searchCost: cfg.search_micro ?? 0,
      searchesPerDay: cfg.searches_per_day ?? 0,
      searchMonthUsed: Boolean(cfg.search_month_used),
    };
  })
  .catch(() => {})
  .then(refreshMe)
  .then(() => {
    showSample();
    const msg = arrivalMessage(query);
    if (msg) setStatus(msg);
  });
